import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import TrafficSignal from './TrafficSignal';

/**
 * Lightweight "smart-city intersection" scene for the hero.
 * - Cars (rounded rects) glide along lanes and pause behind the signal.
 * - A pedestrian crossing sits safely in view.
 * - "AI monitor" rings pulse to suggest automated road monitoring.
 * All motion is SVG transform/opacity only and freezes under reduced-motion.
 */
export default function HeroArt({ className }) {
    const reduce = useReducedMotion();

    return (
        <div className={className} aria-hidden="true">
            <div className="relative w-full max-w-md mx-auto aspect-square">
                {/* soft glow */}
                <div className="absolute inset-0 rounded-[2rem] bg-grad-civic opacity-10 blur-2xl" />

                <svg viewBox="0 0 400 400" className="relative w-full h-full">
                    {/* board */}
                    <rect x="0" y="0" width="400" height="400" rx="32" className="fill-card" />
                    {/* grid streets */}
                    <g className="stroke-border" strokeWidth="2">
                        <line x1="0" y1="150" x2="400" y2="150" />
                        <line x1="0" y1="250" x2="400" y2="250" />
                        <line x1="150" y1="0" x2="150" y2="400" />
                        <line x1="250" y1="0" x2="250" y2="400" />
                    </g>
                    {/* main road */}
                    <rect x="0" y="170" width="400" height="60" fill="hsl(222 30% 24%)" />
                    {/* lane dashes */}
                    <g stroke="hsl(48 96% 76%)" strokeWidth="3" strokeDasharray="16 14">
                        <line x1="0" y1="200" x2="400" y2="200" />
                    </g>
                    {/* pedestrian crossing (safe) */}
                    <g fill="white" opacity="0.95">
                        {[286, 298, 310, 322, 334].map((x) => (
                            <rect key={x} x={x} y="172" width="7" height="56" />
                        ))}
                    </g>

                    {/* AI monitor pings */}
                    {[ { x: 120, y: 96 }, { x: 300, y: 300 } ].map((p, i) => (
                        <motion.circle
                            key={i}
                            cx={p.x}
                            cy={p.y}
                            r="6"
                            fill="none"
                            stroke="hsl(var(--primary))"
                            strokeWidth="2"
                            initial={{ scale: 1, opacity: 0.7 }}
                            animate={reduce ? { opacity: 0.5 } : { scale: [1, 3], opacity: [0.7, 0] }}
                            transition={reduce ? undefined : { duration: 2.4, repeat: Infinity, delay: i * 1.2, ease: 'easeOut' }}
                            style={{ transformOrigin: `${p.x}px ${p.y}px` }}
                        />
                    ))}
                    {[ { x: 120, y: 96 }, { x: 300, y: 300 } ].map((p, i) => (
                        <circle key={`d${i}`} cx={p.x} cy={p.y} r="3.5" fill="hsl(var(--primary))" />
                    ))}

                    {/* cars gliding then stopping behind the crossing */}
                    {[
                        { y: 178, color: 'hsl(var(--primary))', delay: 0 },
                        { y: 204, color: 'hsl(var(--success))', delay: 1.1 },
                    ].map((car, i) => (
                        <motion.rect
                            key={i}
                            y={car.y}
                            width="34"
                            height="18"
                            rx="5"
                            fill={car.color}
                            initial={{ x: -50 }}
                            animate={reduce ? { x: 210 } : { x: [-50, 240, 240, 470] }}
                            transition={
                                reduce
                                    ? undefined
                                    : { duration: 6, times: [0, 0.45, 0.72, 1], repeat: Infinity, delay: car.delay, ease: 'easeInOut' }
                            }
                        />
                    ))}
                </svg>

                {/* signal overlay, anchored at the intersection */}
                <div className="absolute right-6 top-6">
                    <TrafficSignal size={40} />
                </div>
            </div>
        </div>
    );
}
